import { v4 as uuid } from 'uuid';

import { CHANGE_V2_CURRENT_DATE, CHANGE_V2_ID_PREFIX, CHANGE_V2_REQUEST_ID_PREFIX } from '../constants';
import { LIFECYCLE_STEP, TAssessorFunction, TChangeTask, TChangeV2, TContainment, TWorkflowRouteStep } from '../models';

const padSequence = (value: string | number): string => String(value).padStart(4, '0');

// Produces a stable request id for seeded/configured records. Numeric record ids
// retain their sequence; records without one use a short technical-id fragment.
export const deriveRequestId = (id: string, displayId?: string): string => {
  const year = CHANGE_V2_CURRENT_DATE.getUTCFullYear();
  const displayIdMatch = displayId?.match(new RegExp(`^${CHANGE_V2_ID_PREFIX}-(\\d{4})-(\\d+)$`));
  if (displayIdMatch) return `${CHANGE_V2_REQUEST_ID_PREFIX}-${displayIdMatch[1]}-${padSequence(displayIdMatch[2])}`;

  const idSequence = id.match(/(\d+)$/)?.[1];
  if (idSequence) return `${CHANGE_V2_REQUEST_ID_PREFIX}-${year}-${padSequence(idSequence)}`;

  const idFragment = id
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 8)
    .toLocaleUpperCase();
  return `${CHANGE_V2_REQUEST_ID_PREFIX}-${year}-${idFragment}`;
};

// Next request id in the REQ-YYYY-#### series, assigned when a draft is created.
export const generateRequestId = (changes: TChangeV2[]): string => {
  const year = CHANGE_V2_CURRENT_DATE.getUTCFullYear();
  const pattern = new RegExp(`^${CHANGE_V2_REQUEST_ID_PREFIX}-(\\d{4})-(\\d+)$`);
  const nextSequence =
    changes.reduce((highest, change) => {
      const match = change.requestId.match(pattern);
      if (!match || Number(match[1]) !== year) return highest;
      return Math.max(highest, Number(match[2]));
    }, 0) + 1;
  return `${CHANGE_V2_REQUEST_ID_PREFIX}-${year}-${padSequence(nextSequence)}`;
};

// Before request submission the request id is primary; afterwards the generated
// Change ID takes over everywhere the record reference is shown.
export const getChangeReference = (change: TChangeV2): string => change.displayId ?? change.requestId;

// Next human-readable id in the CHG-YYYY-#### series, derived from existing ids.
export const generateDisplayId = (changes: TChangeV2[]): string => {
  const year = CHANGE_V2_CURRENT_DATE.getUTCFullYear();
  const pattern = new RegExp(`^${CHANGE_V2_ID_PREFIX}-(\\d{4})-(\\d+)$`);
  const nextSequence =
    changes.reduce((highest, change) => {
      const match = change.displayId?.match(pattern);
      if (!match || Number(match[1]) !== year) return highest;
      return Math.max(highest, Number(match[2]));
    }, 0) + 1;
  return `${CHANGE_V2_ID_PREFIX}-${year}-${padSequence(nextSequence)}`;
};

// The generated route prepared from the request. Each step carries a short reason so the
// route is explainable and never silently imposed.
export const buildWorkflowRoute = (input: {
  assessorFunctions: TAssessorFunction[];
  containmentRequired: boolean;
  hasTemplate: boolean;
}): TWorkflowRouteStep[] => {
  const assessorSummary =
    input.assessorFunctions.length > 0
      ? `Assessment tasks for ${input.assessorFunctions.length} selected function${
          input.assessorFunctions.length > 1 ? 's' : ''
        }.`
      : 'At least one assessor function is required before this step runs.';

  return [
    { step: LIFECYCLE_STEP.REQUEST, reason: 'The initiator described the proposed change.' },
    { step: LIFECYCLE_STEP.ASSESSMENT, reason: assessorSummary },
    {
      step: LIFECYCLE_STEP.PLANNING,
      reason: input.containmentRequired
        ? 'Initial planning follows assessment approval. Immediate containment can start in parallel.'
        : 'Initial planning follows assessment approval.',
    },
  ];
};

export type TAutomationPreviewItem = {
  id: string;
  label: string;
  detail?: string;
  willRun: boolean;
  /** A regulatory guardrail note when the automation deliberately does NOT decide something. */
  note?: string;
};

// The reviewable list of what acceptance will do. Nothing here decides a
// regulatory question; those remain human decisions.
export const buildAutomationPreview = (input: {
  assessorFunctions: TAssessorFunction[];
  changeManager?: string;
  hasTemplate: boolean;
  containmentRequired: boolean;
}): TAutomationPreviewItem[] => [
  { id: 'gen-id', label: 'Generate change ID', detail: 'Assigns the next CHG-2026-#### identifier.', willRun: true },
  {
    id: 'assign-manager',
    label: 'Assign change manager',
    detail: input.changeManager ? `Assigns ${input.changeManager}.` : 'Select a change manager to enable this.',
    willRun: Boolean(input.changeManager),
  },
  {
    id: 'apply-template',
    label: 'Apply template suggestions',
    detail: input.hasTemplate
      ? 'Adds the template assessment sections and suggested tasks for review.'
      : 'No template selected.',
    willRun: input.hasTemplate,
  },
  {
    id: 'assessment-tasks',
    label: 'Create assessment tasks',
    detail:
      input.assessorFunctions.length > 0
        ? `One task per selected function (${input.assessorFunctions.length}).`
        : 'Select at least one assessor function.',
    willRun: input.assessorFunctions.length > 0,
  },
  {
    id: 'containment-task',
    label: 'Create urgent containment task',
    detail: input.containmentRequired ? 'Creates a containment task that can start immediately.' : 'Not required.',
    willRun: input.containmentRequired,
  },
  {
    id: 'notify',
    label: 'Notify initiator and assigned functions',
    detail: 'Sends prototype notifications; no external parties are contacted.',
    willRun: true,
  },
  {
    id: 'move-status',
    label: 'Move change to in assessment',
    detail: 'Advances the lifecycle to assessment.',
    willRun: true,
    note: 'CAPA necessity, reportability and notified-body involvement stay manual decisions.',
  },
];

export const createAssessmentTasks = (assessorFunctions: TAssessorFunction[]): TChangeTask[] =>
  assessorFunctions.map((assessorFunction) => ({
    id: uuid(),
    title: `Assessment: ${assessorFunction}`,
    kind: 'assessment',
    assignedFunction: assessorFunction,
    status: 'Open',
  }));

export const createContainmentTask = (containment: TContainment): TChangeTask | undefined => {
  if (!containment.required) return undefined;
  return {
    id: uuid(),
    title: containment.description ? `Containment: ${containment.description}` : 'Immediate containment',
    kind: 'containment',
    owner: containment.owner,
    dueDate: containment.dueDate,
    status: 'Open',
    urgent: true,
  };
};
